/**
 * Which signing key a client uses.
 *
 * There are only two answers, and getting them the wrong way round is expensive: an app already
 * on Play can only be updated by the key it was first signed with, so uploading is the only
 * option there. A brand-new app needs a key generated once and then kept forever. The choice is
 * put in front of the person rather than inferred, because the consequence of guessing is an app
 * that can never be updated again.
 */
import { useState } from 'react';
import { ApiError, api, type KeystoreResult } from '../api';

export type KeystoreMode = 'generate' | 'upload' | 'later';

export function KeystoreChoiceFields({ mode, onMode, slug }: {
  mode: KeystoreMode;
  onMode: (mode: KeystoreMode) => void;
  slug: string;
}) {
  return (
    <fieldset className="choice">
      <legend>Signing key</legend>

      <label className="option">
        <input type="radio" name="keystoreMode" value="generate" checked={mode === 'generate'}
               onChange={() => onMode('generate')} />
        <span>
          <strong>Generate a new one</strong>
          <em>For an app not on Play yet. Kept forever.</em>
        </span>
      </label>

      <label className="option">
        <input type="radio" name="keystoreMode" value="upload" checked={mode === 'upload'}
               onChange={() => onMode('upload')} />
        <span>
          <strong>Upload the existing one</strong>
          <em>For an app already on Play. Anything else is rejected at upload.</em>
        </span>
      </label>

      {mode === 'upload' && (
        <div className="option-body">
          <div className="field">
            <label htmlFor={`ks-file-${slug}`}>Keystore file</label>
            <input id={`ks-file-${slug}`} name="keystore" type="file"
                   accept=".jks,.keystore,.p12,.pfx" required />
          </div>
          <div className="pair">
            <div className="field">
              <label htmlFor={`ks-alias-${slug}`}>Key alias</label>
              <input id={`ks-alias-${slug}`} name="keyAlias" type="text" required placeholder="upload" />
            </div>
            <div className="field">
              <label htmlFor={`ks-store-${slug}`}>Store password</label>
              <input id={`ks-store-${slug}`} name="storePassword" type="password" required />
            </div>
          </div>
          <div className="field">
            <label htmlFor={`ks-key-${slug}`}>Key password</label>
            <input id={`ks-key-${slug}`} name="keyPassword" type="password" />
            <span className="hint">Blank if it matches the store password.</span>
          </div>
        </div>
      )}

      <label className="option">
        <input type="radio" name="keystoreMode" value="later" checked={mode === 'later'}
               onChange={() => onMode('later')} />
        <span>
          <strong>Decide later</strong>
          <em>The first release build generates one if none exists by then.</em>
        </span>
      </label>
    </fieldset>
  );
}

/** Shown once a key exists or has just been made: the fingerprint is the useful part. */
export function KeystoreSummary({ result, onDone }: { result: KeystoreResult; onDone: () => void }) {
  return (
    <div className="section keynote">
      <p>
        {result.generated ? 'New ' : ''}{result.storeType} keystore stored, alias{' '}
        <span className="mono">{result.alias}</span>.
        {result.replacedPrevious && ' The previous key was kept, not deleted.'}
      </p>
      <p className="fingerprint mono">{result.sha256}</p>
      <p className="note">{result.note}</p>
      <button type="button" className="btn quiet" onClick={onDone}>Done</button>
    </div>
  );
}

/** The generate action on a client that has no key yet. */
export function GenerateKey({ slug, onDone, onToast }: {
  slug: string;
  onDone: () => void;
  onToast: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<KeystoreResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (result) return <KeystoreSummary result={result} onDone={onDone} />;

  return (
    <div className="section">
      {error && <div className="errors"><span>{error}</span></div>}
      <button
        type="button"
        className="btn"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setError(null);
          api.generateKeystore(slug)
            .then((value) => {
              setResult(value);
              onToast('Signing key created');
            })
            .catch((failure: ApiError) => setError(failure.message))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Creating the key' : 'Generate a signing key now'}
      </button>
      <p className="hint" style={{ marginTop: 8 }}>
        The fingerprint is needed for the Maps key restriction.
      </p>
    </div>
  );
}
